import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';
import ScrollLock from 'react-scrolllock';

type ModalBackgroundTypes = {
  className?: String,
  children?: React.ReactNode,
  onClick: React.MouseEventHandler<HTMLDivElement> | undefined,
} 

type ModalOverlayTypes = {
  animationKey: String,
  className?: String,
  children: React.ReactElement,
  onModalToggle: Function,
  active: boolean
  useScrollLock?: boolean,
}

type ScrollLockWrapperType = {
  children: React.ReactElement,
  isActive: boolean,
  accountForScrollbars?: boolean
}

const ScrollLockWrapper: React.FC<ScrollLockWrapperType> = ({ children, isActive = false, accountForScrollbars = false }) => (
  isActive ? (
    <ScrollLock accountForScrollbars={accountForScrollbars}>
      {children}
    </ScrollLock>
  ) : null
);

const ModalBackground: React.FC<ModalBackgroundTypes> = ({
  className = '', children = [], onClick
}) => (
  <div
    role="presentation"
    onClick={onClick}
    className={classNames('z-40 fixed h-full w-full bg-border-darkest/50', className as string)}
  >
    {children}
  </div>
)

export const ModalOverlay: React.FC<ModalOverlayTypes> = ({
  animationKey,
  className = '',
  children,
  onModalToggle = () => { },
  active = false,
  useScrollLock = true,
}) => {
  const [isActive, setActive] = useState(active);
  const [animationState, setAnimation] = useState(null);

  useEffect(() => setActive(active), [active])

  useEffect(() => {
    const onKeyDown = (e: any) => {
      if (e.key === 'Escape') {
        setAnimation('fade-out' as any);
      }
    }
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (isActive) {
      setActive(true);
      setAnimation('fade-in' as any);
    } else {
      setAnimation('fade-out' as any)
    }
  }, [isActive])

  useEffect(() => {
    if (active !== isActive && onModalToggle) {
      onModalToggle(isActive)
    }
  }, [active, isActive, onModalToggle])

  const onAnimationEnd = useCallback(() => {
    if (animationState === 'fade-out') {
      setActive(false);
    }
    setAnimation(null);
  }, [animationState])

  const onOverlayClick = useCallback(() => setAnimation('fade-out' as any), [])

  return (
    <div
      key={animationKey as any}
      className={classNames(
        { 'z-50 fixed h-full w-full top-0 bottom-0 left-0 right-0': isActive } as any,
        { [`animate-${animationState}`]: animationState },
        className
      )}
      onAnimationEnd={onAnimationEnd}
    >
      <div className={isActive ? 'p-4 fixed z-50 h-full w-full top-0 bottom-0 left-0 right-0' : ''}>
        <div className="flex flex-col justify-center items-center h-full w-full">
          {
            useScrollLock ? (
              <ScrollLockWrapper isActive={isActive}>
                {children}
              </ScrollLockWrapper>
            ) : children
          }
        </div>
      </div>
      {
        isActive ? (
          <ModalBackground onClick={onOverlayClick} />
        ) : null
      }
    </div>
  )
}

export default ModalOverlay;
